import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Space, Table, Tag, Tooltip } from 'antd';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { DataTableColumns } from '../../../../components/molecules/DataTable/DataTable';
import { UserAvatar } from '../../../../components/atoms/UserAvatar';
import { useConnection, useUserInfo } from '../../../../connection/Application';
import { Team } from '../../../../entity/Team';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { useQueryParam } from 'use-query-params';
import { useHistory } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';

const columns = (onSelect: (team: Team) => void): DataTableColumns<Team> => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    // eslint-disable-next-line react/display-name
    render: (value, record: any) => (
      <Space>
        <UserAvatar type={'team'} src={record.image} />
        <Button disabled={!record.access} type={'link'} onClick={() => onSelect(record)}>
          {record.name}
        </Button>
      </Space>
    ),
  },
  {
    title: 'Type',
    dataIndex: 'teamType',
    key: 'teamType',
    // eslint-disable-next-line react/display-name
    render: (value) => <Tag color={value?.color}>{value?.name}</Tag>,
  },
  {
    title: 'Leads',
    dataIndex: 'leads',
    key: 'leads',
    // eslint-disable-next-line react/display-name
    render: (value, record) => (
      <>
        {record.leads.map((lead) => (
          <Tooltip key={lead.id} title={`${lead.firstName} ${lead.lastName}`}>
            <UserAvatar src={lead.image} />
          </Tooltip>
        ))}
      </>
    ),
  },
  {
    title: 'Members',
    dataIndex: 'memberCount',
    key: 'memberCount',
    align: 'right',
    // eslint-disable-next-line react/display-name
    render: (value, record) => (isNaN(record.memberCount) ? 0 : record.memberCount),
  },
];

const updateTeamMemberCount = (team: Team): number => {
  const childMemberCount =
    team.children?.map((t) => updateTeamMemberCount(t)).reduce((a, b) => a + b) ?? 0;
  team.memberCount = team.memberCount + childMemberCount;
  return team.memberCount ?? 0;
};

const getSortOrder = (prop: string): ((a: any, b: any) => number) => {
  return (a, b) => {
    if (a[prop] > b[prop]) {
      return 1;
    } else if (a[prop] < b[prop]) {
      return -1;
    }
    return 0;
  };
};

const buildTeamsStructure = (allTeams: Team[]): Team[] => {
  const teamsCopy: Team[] = JSON.parse(JSON.stringify(allTeams));
  const teams: Team[] = [];
  const teamsMap: Record<number, Team> = Object.fromEntries(teamsCopy.map((t) => [t.id, t]));

  for (const t of teamsCopy.sort(getSortOrder('id'))) {
    (t as any).key = t.id;
    if (t.parentId !== undefined) {
      if (teamsMap[t.parentId].children === undefined) {
        teamsMap[t.parentId].children = [];
      }
      teamsMap[t.parentId].children.push(t);
    } else {
      teams.push(t);
    }
  }
  for (const t of teams) {
    updateTeamMemberCount(t);
  }
  return teams;
};

export const SetupPage: FC = () => {
  const connection = useConnection();
  const user = useUserInfo();
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const [loading, setLoading] = useState<boolean>(false);
  const [teamStructure, setTeamStructure] = useState<Team[]>([]);
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const cycleRef = useRef(cycle);
  const history = useHistory();
  const [expandedKeys, setExpandedKeys] = useState<number[]>([]);

  useEffect(() => {
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  const fetchTeams = useCallback(() => {
    const isAdminOrHr = user?.roles?.includes('admin') || user?.roles?.includes('hr');
    const endpoint = 'teams';
    connection
      .get(endpoint, { size: 1000, cycleId: cycle })
      .then(async (value) => {
        let x: any[] = [];
        if (!isAdminOrHr) {
          x = (await connection.get('teams/user/scope', { size: 1000, cycleId: cycle })).teams;
        }
        const teamsData = value.data;
        const teamStructure = buildTeamsStructure(
          teamsData.map((e: any) => ({
            ...e,
            key: e.id,
            access: isAdminOrHr ? true : !!x.find((ss) => ss.id === e.id),
          })),
        );
        setTeamStructure(teamStructure);
        setExpandedKeys(teamsData.map((v: any) => v.id));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [connection, cycle, user]);

  useEffect(() => fetchTeams(), [fetchTeams]);

  const onRowClicked = useCallback(
    (team: any) => {
      if (team?.access) {
        history.push(
          `/webux/setup/team-setup/${team.id}/${encodeURIComponent(team.name)}?cycle=${cycle}`,
        );
      }
    },
    [cycle, history],
  );

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="360° Configuration"
        subTitle="Configure 360° parameters of organization, departments, and teams."
      />
      <WebUxPageContent>
        <WebUxPageItem>
          <Space direction={'vertical'} style={{ width: '100%', marginBottom: 16 }} size={16}>
            <FlexBox horizontalAlign={'left'}>
              <CycleToggle
                value={parseInt(`${cycle}`)}
                onChange={(value) => setCycle(value, 'replace')}
                data={cycles}
              />
            </FlexBox>
          </Space>
          <Table
            size="small"
            loading={loading}
            columns={useMemo(() => columns(onRowClicked), [onRowClicked])}
            dataSource={teamStructure}
            pagination={false}
            expandable={{
              indentSize: 36,
              expandedRowKeys: expandedKeys,
              // defaultExpandAllRows: true,
              // eslint-disable-next-line react/display-name
              expandIcon: ({ expanded, onExpand, record }) => (
                <>
                  {record?.children?.length > 0 &&
                    record?.children &&
                    (expanded ? (
                      <span style={{ marginRight: 10 }}>
                        <CaretDownOutlined
                          key={record.id}
                          onClick={(e: any) => {
                            onExpand(record, e);
                            setExpandedKeys(expandedKeys.filter((v) => v !== record.id));
                          }}
                        />
                      </span>
                    ) : (
                      <span style={{ marginRight: 10 }}>
                        <CaretRightOutlined
                          key={record.id}
                          onClick={(e: any) => {
                            onExpand(record, e);
                            setExpandedKeys([...expandedKeys, record.id]);
                          }}
                        />
                      </span>
                    ))}
                  {(record?.children?.length == 0 || !record?.children) && (
                    <span key={record.id} style={{ marginRight: 23 }} />
                  )}
                </>
              ),
            }}
          />
        </WebUxPageItem>
      </WebUxPageContent>
    </WebUxPage>
  );
};
